import { useEffect, useState } from 'react';
import Notification from '@rio-cloud/rio-uikit/Notification';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import { useIntl } from 'react-intl';
import useWindowResize from '@rio-cloud/rio-uikit/useWindowResize';
import { useAppDispatch } from '../configuration/setup/hooks';
import { showChangesDetectedDialog } from '../data/appSlice';
import { useUserAccount, useUserProfile } from '../configuration/login/loginSlice';
import { useUpdateEulaStatusMutation } from '../services/iamApi';
import { getChannelFromLocation } from '../utils';
import { eulaConfig } from '../features/man-eula/config/eulaConfig';
import { extractUserProfileIdFromSubject } from '../features/man-eula/EulaConsentFooter';
import { useRelevantAssets } from '../services/assetsApi';
import { reportErrorToSentry } from '../configuration/setup/sentry';

export const useHandlePostError = (error: FetchBaseQueryError | SerializedError | undefined) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (error) {
            if ('status' in error) {
                if (error.status === 401) {
                    Notification.error(
                        intl.formatMessage({ id: 'intl-msg:man-eula.error.unauthorized.notification.message' }),
                        intl.formatMessage({ id: 'intl-msg:man-eula.error.unauthorized.notification.title' })
                    );
                } else if (error.status === 403) {
                    Notification.error(
                        intl.formatMessage({ id: 'intl-msg:man-eula.error.forbidden.notification.message' }),
                        intl.formatMessage({ id: 'intl-msg:man-eula.error.forbidden.notification.title' })
                    );
                } else if (error.status === 404) {
                    Notification.error(
                        intl.formatMessage({ id: 'intl-msg:man-eula.error.notFound.notification.message' }),
                        intl.formatMessage({ id: 'intl-msg:man-eula.error.notFound.notification.title' })
                    );
                } else if (error.status === 412) {
                    dispatch(showChangesDetectedDialog());
                } else {
                    Notification.error(
                        intl.formatMessage(
                            {
                                id: 'intl-msg:man-eula.error.generic.notification.messageWithStatusCode',
                            },
                            { statusCode: error.status }
                        ),
                        'Error'
                    );
                    reportErrorToSentry(new Error('Could not update EULA status'), {
                        extra: {
                            fetchBaseQueryError: error,
                            status: error?.status,
                            data: error?.data,
                            endpoint: 'api.iam.rio.cloud/accounts',
                        },
                    });
                }
            } else {
                reportErrorToSentry(error);
            }
        }
    }, [error]);
};

export const useIsXsScreen = () => {
    const [isXsScreen, setIsXsScreen] = useState(window.innerWidth < 480);
    useWindowResize(() => {
        if (window.innerWidth < 480) {
            setIsXsScreen(true);
        } else {
            setIsXsScreen(false);
        }
    });

    return isXsScreen;
};

export const useUpdateEulaStatus = (etag: string | undefined) => {
    const accountId = useUserAccount();
    const userProfile = useUserProfile();
    const assets = useRelevantAssets();

    const [updateEulaStatus, updateEulaStatusResult] = useUpdateEulaStatusMutation();

    const updateEulaStatusInBackend = (updatedStatus: 'ACCEPTED' | 'DECLINED') => {
        if (
            accountId &&
            userProfile?.givenName &&
            userProfile.familyName &&
            userProfile.email &&
            userProfile.sub &&
            etag
        ) {
            updateEulaStatus({
                accountId: accountId,
                etag: etag,
                status: updatedStatus,
                channel: getChannelFromLocation(window.location),
                eulaVersion: eulaConfig.version,
                firstName: userProfile.givenName,
                lastName: userProfile.familyName,
                contactInfo: userProfile.email,
                profileId: extractUserProfileIdFromSubject(userProfile.sub),
                vinSnapshot: assets?.map(it => it.identification).filter(it => it !== undefined) || [],
            });
        } else {
            if (!accountId) {
                console.error('Account ID is not available.');
                reportErrorToSentry('Mutation Error: Account ID is not available.');
            } else if (!userProfile) {
                console.error('User profile is not available.');
                reportErrorToSentry('Mutation Error: User profile is not available.');
            } else if (userProfile && !userProfile.givenName) {
                console.error('User profile first name is not available.');
                reportErrorToSentry('Mutation Error: User profile first name is not available.');
            } else if (userProfile && !userProfile.familyName) {
                console.error('User profile last name is not available.');
                reportErrorToSentry('Mutation Error: User profile last name is not available.');
            } else if (userProfile && !userProfile.email) {
                console.error('User profile email is not available.');
                reportErrorToSentry('Mutation Error: User profile email is not available.');
            } else if (userProfile && !userProfile.sub) {
                console.error('User profile subject is not available.');
                reportErrorToSentry('Mutation Error: User profile subject is not available.');
            } else {
                console.error('Unknown error occurred while handling EULA acceptance.');
                reportErrorToSentry('Mutation Error: Unknown error occurred while handling EULA acceptance.');
            }
        }
    };

    return [updateEulaStatusInBackend, updateEulaStatusResult] as const;
};
